import { useQuery } from "@tanstack/react-query";
import type { PetWithStatus } from "@shared/domain/petModels";
import { fetchOwnerPets } from "../api/petBackendApi";
import { petNeedsAttention } from "../utils/petHelpers";

/** How often the global attention indicator re-checks the roster (ms). */
const ATTENTION_POLL_INTERVAL_MS = 60_000;
/** Consider cached attention data fresh for this long (ms). */
const ATTENTION_STALE_TIME_MS = 30_000;

export interface AttentionState {
  /** Pets actively asking for care right now (shares petNeedsAttention with cards/banner). */
  attentionPets: PetWithStatus[];
  /** Convenience: number of pets needing care. */
  count: number;
  /** The first pet needing care, if any — the tap target for the HUD alert. */
  primary: PetWithStatus | null;
  isLoading: boolean;
}

/**
 * Global "a Suikamon needs you" signal, decoupled from PlayPage's local roster state so the
 * persistent game HUD can surface it from anywhere. Uses the same `petNeedsAttention`
 * predicate as the per-card label and the Play-page banner, so all three always agree.
 *
 * `actingAddress` gates on the authenticated identity / guest sentinel (never a Sui wallet).
 */
export function useAttentionPets(actingAddress: string | null): AttentionState {
  const { data, isLoading } = useQuery({
    queryKey: ["attentionPets", actingAddress],
    queryFn: () => fetchOwnerPets(actingAddress as string),
    enabled: !!actingAddress,
    staleTime: ATTENTION_STALE_TIME_MS,
    refetchInterval: ATTENTION_POLL_INTERVAL_MS,
  });

  const attentionPets = (data ?? []).filter(petNeedsAttention);

  return {
    attentionPets,
    count: attentionPets.length,
    primary: attentionPets[0] ?? null,
    isLoading,
  };
}
